import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TokenFailure, useApiAuth } from '../../hooks/useAxiosSwagger';
import './ApiAccessNotice.scss';

interface Advice {
    headline: string;
    explanation: string;
    actionLabel: string;
    /** True when the action opens an Auth0 window instead of retrying silently. */
    interactive: boolean;
}

/**
 * What to tell the user, and what to offer them, per failure.
 *
 * The codes are worth splitting on because the answer differs: consent is a one-off
 * approval, an expired session is a login, and a network hiccup is neither.
 */
const adviseOn = (failure: TokenFailure): Advice => {
    switch (failure.code) {
        case 'consent_required':
            return {
                headline: 'Dit dashboard heeft nog geen toegang tot de API',
                explanation:
                    'Je Auth0-account moet die toegang eenmalig goedkeuren. Auth0 kan dat niet op de achtergrond vragen, dus het gebeurt in een apart venster.',
                actionLabel: 'Toegang verlenen',
                interactive: true,
            };
        case 'login_required':
            return {
                headline: 'Je Auth0-sessie is verlopen',
                explanation:
                    'Meld je opnieuw aan om verder te kunnen. Het dashboard blijft ondertussen staan waar het staat.',
                actionLabel: 'Opnieuw aanmelden',
                interactive: true,
            };
        default:
            return failure.needsInteraction
                ? {
                      headline: 'Auth0 heeft nog een stap nodig',
                      explanation:
                          'Er is iets dat je zelf moet beantwoorden voordat het dashboard een token krijgt. Dat gebeurt in een apart venster.',
                      actionLabel: 'Verdergaan in Auth0',
                      interactive: true,
                  }
                : {
                      headline: 'Het ophalen van een toegangstoken is mislukt',
                      explanation:
                          'Zonder token blijven de gegevens hieronder leeg of verouderd. Vaak is het tijdelijk en helpt het om het opnieuw te proberen.',
                      actionLabel: 'Opnieuw proberen',
                      interactive: false,
                  };
    }
};

/**
 * The banner that appears when the dashboard has no access token.
 *
 * It exists because the thing standing in the way - consent for the API - can only be
 * given by the person looking at the screen, and Auth0 has no way to ask for it from a
 * hidden iframe. So the app asks here instead, in a window a click can open.
 */
export const ApiAccessNotice = () => {
    const { failure, busy, grantAccess, retry } = useApiAuth();

    if (!failure) {
        return null;
    }

    const advice = adviseOn(failure);

    const handleClick = () => {
        if (advice.interactive) {
            void grantAccess();

            return;
        }

        retry();
    };

    return (
        <div className="api-access" role="alert">
            <FontAwesomeIcon
                className="api-access__icon"
                icon="triangle-exclamation"
            />

            <div className="api-access__body">
                <p className="api-access__headline">{advice.headline}</p>
                <p className="api-access__explanation">{advice.explanation}</p>

                {advice.interactive && (
                    <p className="api-access__hint">
                        Gebeurt er niets? Sta pop-ups toe voor deze site en
                        probeer het opnieuw.
                    </p>
                )}

                {/*
                    The error stays on screen, verbatim. Replacing it with a friendlier
                    sentence is exactly what leaves the next person guessing - this line is
                    the one that gets pasted into a search box or an Auth0 log filter.
                */}
                <p className="api-access__detail">
                    {failure.code ? `${failure.code}: ` : ''}
                    {failure.message}
                </p>
            </div>

            <button
                type="button"
                className="api-access__button"
                onClick={handleClick}
                disabled={busy}
            >
                {busy && <FontAwesomeIcon icon="spinner" spin />}
                {advice.actionLabel}
            </button>
        </div>
    );
};
